import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import moment from 'moment';
import firebase from 'src/utils/firebase';
import { buildApiUrl } from 'src/utils/api';
import { getActiveShopId } from 'src/utils/shopContext';
import {
  currentListingStatusScope, emitListingStatusRequest,
  prepareListingStatusIntent, recordListingStatusResult
} from 'src/utils/listingStatusIntent';
import { socketClient } from '../index';

const categories = ['inventory', 'testdrives', 'financeleads', 'contracts', 'statistics', 'reports', 'rin', 'livechat'];
const defaultCategory = 'inventory';
export const dealershipMetricPeriodOptions = [
  { value: 'current_month', label: 'Current Month' },
  { value: 'ytd', label: 'YTD' },
  { value: 'last_12_months', label: 'Last 12 Months' },
  { value: 'last_month', label: 'Last Month' }
];
export const dealershipListFilterOptions = {
  inventory: [
    { value: 'all', label: 'All Inventory' },
    { value: 'with_issues', label: 'With Issues' },
    { value: 'missing_cost', label: 'Missing Cost' },
    { value: 'missing_vin', label: 'Missing VIN' },
    { value: 'missing_carfax', label: 'Missing CARFAX' },
    { value: 'missing_mileage', label: 'Missing Mileage' },
    { value: 'age_0_30', label: 'Age 0-30 Days' },
    { value: 'age_31_60', label: 'Age 31-60 Days' },
    { value: 'age_61_90', label: 'Age 61-90 Days' },
    { value: 'age_91_plus', label: 'Age 91+ Days' },
    { value: 'stale_inventory', label: 'Stale Inventory' },
    { value: 'for_sale', label: 'For Sale' },
    { value: 'sold', label: 'Sold' }
  ],
  contracts: [
    { value: 'all', label: 'All Contracts' },
    { value: 'missing_cost', label: 'Missing Cost' },
    { value: 'missing_vin', label: 'Missing VIN' },
    { value: 'missing_carfax', label: 'Missing CARFAX #' },
    { value: 'missing_license', label: 'Missing Licence #' },
    { value: 'unmatched_listing', label: 'No Matched Listing' }
  ],
  financeleads: [
    { value: 'all', label: 'All Finance Leads' },
    { value: 'missing_contact', label: 'Missing Contact' },
    { value: 'missing_phone', label: 'Missing Phone' },
    { value: 'missing_email', label: 'Missing Email' }
  ],
  testdrives: [
    { value: 'all', label: 'All Test Drives' },
    { value: 'missing_contact', label: 'Missing Contact' },
    { value: 'missing_phone', label: 'Missing Phone' },
    { value: 'missing_email', label: 'Missing Email' }
  ]
};
const defaultQuery = {
  page: 1,
  pageSize: 20,
  search: '',
  filter: 'all',
  source: 'all',
  brand: '',
  model: ''
};

const createDefaultQueryMap = () => categories.reduce((acc, category) => {
  acc[category] = { ...defaultQuery };
  return acc;
}, {});

const createDefaultPaginationMap = () => categories.reduce((acc, category) => {
  acc[category] = {
    total: 0,
    page: 1,
    pageSize: defaultQuery.pageSize,
    totalPages: 1,
    hasPrev: false,
    hasNext: false
  };
  return acc;
}, {});

const buildRequestId = (scope) =>
  `dealership:${scope}:${Date.now()}:${Math.random().toString(36).slice(2)}`;

const getOptionalIdToken = async () => {
  try {
    const currentUser = firebase.auth().currentUser;
    return currentUser ? await currentUser.getIdToken() : null;
  } catch (_error) {
    return null;
  }
};

const getDealershipAuthConfig = async (config = {}) => {
  const idToken = await getOptionalIdToken();
  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {})
    }
  };
};

const emitScopedDealershipRequest = ({
  emitEvent,
  successEvent,
  errorEvent,
  payload,
  timeoutMessage,
  mapSuccess
}) => new Promise((resolve, reject) => {
  const handleSuccess = (response) => {
    if (response?.requestId !== payload.requestId) {
      return;
    }

    clearTimeout(timeoutId);
    socketClient.off(successEvent, handleSuccess);
    socketClient.off(errorEvent, handleError);
    resolve(mapSuccess ? mapSuccess(response) : response);
  };

  const handleError = (response) => {
    if (response?.requestId !== payload.requestId) {
      return;
    }

    clearTimeout(timeoutId);
    socketClient.off(successEvent, handleSuccess);
    socketClient.off(errorEvent, handleError);
    reject(new Error(response?.message || timeoutMessage));
  };

  const timeoutId = setTimeout(() => {
    socketClient.off(successEvent, handleSuccess);
    socketClient.off(errorEvent, handleError);
    reject(new Error(timeoutMessage));
  }, 10000);

  socketClient.on(successEvent, handleSuccess);
  socketClient.on(errorEvent, handleError);
  socketClient.emit(emitEvent, payload);
});

const normalizePagination = (pagination, fallbackPage, fallbackPageSize, itemCount) => {
  if (pagination) {
    const total = Number(pagination.total || 0);
    const page = Number(pagination.page || fallbackPage || 1);
    const pageSize = Number(pagination.pageSize || fallbackPageSize || defaultQuery.pageSize);
    const totalPages = Number(pagination.totalPages || Math.max(1, Math.ceil(total / Math.max(1, pageSize))));

    return {
      total,
      page,
      pageSize,
      totalPages,
      hasPrev: Boolean(pagination.hasPrev ?? page > 1),
      hasNext: Boolean(pagination.hasNext ?? page < totalPages)
    };
  }

  const total = Number(itemCount || 0);
  const page = Number(fallbackPage || 1);
  const pageSize = Number(fallbackPageSize || defaultQuery.pageSize);
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, pageSize)));

  return {
    total,
    page,
    pageSize,
    totalPages,
    hasPrev: page > 1,
    hasNext: page < totalPages
  };
};

const formatCurrencySummary = (value) => {
  const numeric = Number(value);

  if (!Number.isFinite(numeric)) {
    return value || 'N/A';
  }

  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0
  }).format(numeric);
};

const normalizeInventoryListPayload = (payload) =>
  Array.isArray(payload) ? payload : payload?.inventory || payload?.inventoryList || [];

const buildRecipient = (email) => ({
  name: 'Redline Auto Mall',
  email,
  avatar: null,
  avatarType: 'vehicle'
});

const inventoryToMail = (listing) => {
  const activeReservation = listing.activeViewingReservation || null;
  const repairCost = listing.dealershipRepairCost || listing.salesContractSummary?.dealershipRepairCost;
  const totalVehicleCost = listing.totalVehicleCost || listing.salesContractSummary?.totalVehicleCost;
  const reservationSummary = activeReservation
    ? `${activeReservation.appointmentTypeLabel || 'Viewing'} ${activeReservation.scheduledForLabel || ''}`.trim()
    : '';
  const costSummaryParts = [
    listing.cost ? `Cost ${formatCurrencySummary(listing.cost)}` : null,
    repairCost ? `Repairs ${formatCurrencySummary(repairCost)}` : null,
    totalVehicleCost ? `Total Cost ${formatCurrencySummary(totalVehicleCost)}` : null,
    listing.salesContractSummary?.grossProfit ? `Profit ${formatCurrencySummary(listing.salesContractSummary.grossProfit)}` : null
  ].filter(Boolean);
  const costContent = [
    `<p>Cost: ${listing.cost ? formatCurrencySummary(listing.cost) : 'N/A'}</p>`,
    repairCost ? `<p>Repairs: ${formatCurrencySummary(repairCost)}</p>` : '',
    totalVehicleCost ? `<p>Total Cost: ${formatCurrencySummary(totalVehicleCost)}</p>` : ''
  ].join('');

  return {
    category: 'inventory',
    content: `<p>${formatCurrencySummary(listing.retail_price)}</p><p>Stock Number: ${listing.vehicle?.stockNumber || 'N/A'}</p>${costContent}${activeReservation ? `<p><strong>Reserved:</strong> ${reservationSummary}${activeReservation.customerName ? ` for ${activeReservation.customerName}` : ''}</p>` : ''}<a href="${listing.carfax}" target="_blank" rel="noreferrer">View CARFAX</a>`,
    subject: `${listing.vehicle?.stockNumber ? `Stock #${listing.vehicle.stockNumber}` : listing.status}`,
    summary: `${formatCurrencySummary(listing.retail_price)}${costSummaryParts.length ? ` • ${costSummaryParts.join(' • ')}` : ''}${listing.mileage ? ` • ${listing.mileage} KM` : ''}${listing.status ? ` • ${listing.status}` : ''}${reservationSummary ? ` • Reserved ${reservationSummary}` : ''}`,
    listing,
    vehicle: listing.vehicle,
    date: moment(listing.createdAt).format('YYYY-MM-DD'),
    from: {
      name: listing.title,
      email: 'inventory@redlineautomall.ca',
      avatar: listing?.vehicle_listings_photos?.[0]?.url || null,
      avatarType: 'vehicle',
      displayAvatar: 1
    },
    id: String(listing.id),
    opened: false,
    to: [buildRecipient('info@redlineautomall.ca')],
    tagIds: ['1']
  };
};

const financeToMail = (application) => ({
  category: 'financeleads',
  content: `<p>${application.creditRating || 'N/A'}</p><p>Vehicle: ${application.carType || 'N/A'}</p>`,
  subject: `${application.carType || 'Finance Lead'}`,
  summary: `${application.phoneNumber || application.email || 'No contact'}${application.creditRating ? ` • ${application.creditRating}` : ''}`,
  application,
  date: moment(application.createdAt).format('YYYY-MM-DD'),
  from: {
    name: `${application.firstName || ''} ${application.lastName || ''}`.trim() || 'Applicant',
    email: `${application.phoneNumber || application.email || ''}`,
    avatar: null,
    avatarType: 'person',
    displayAvatar: 1
  },
  id: String(application.id),
  opened: false,
  to: [buildRecipient('info@redlineautohaus.ca')],
  tagIds: ['1']
});

const testDriveToMail = (testDrive) => {
  const linkedListing = testDrive?.vehicle_listing || testDrive?.listing || null;
  const linkedVehicle = linkedListing?.vehicle || null;
  const linkedVehicleDescriptor = [
    linkedVehicle?.year || null,
    linkedVehicle?.make || null,
    linkedVehicle?.model || null
  ].filter(Boolean).join(' ').trim();

  return {
    category: 'testdrives',
    content: `<p>${testDrive.financing ? `Financing: ${testDrive.financing}` : 'Financing preference not provided'}</p><p>Vehicle: ${linkedVehicleDescriptor || linkedListing?.title || 'Not linked to a vehicle listing'}</p>`,
    subject: linkedVehicleDescriptor || linkedListing?.title || 'Test Drive Request',
    summary: `${testDrive.phone || testDrive.email || 'No contact'}${testDrive.financing ? ` • Financing ${testDrive.financing}` : ''}${linkedVehicle?.stockNumber ? ` • Stock #${linkedVehicle.stockNumber}` : ''}`,
    testDrive,
    date: moment(testDrive.createdAt).format('YYYY-MM-DD'),
    from: {
      name: testDrive.name || 'Test Drive Prospect',
      email: testDrive.email || testDrive.phone || '',
      avatar: linkedListing?.vehicle_listings_photos?.[0]?.url || null,
      avatarType: linkedListing ? 'vehicle' : 'person',
      displayAvatar: 1
    },
    id: String(testDrive.id),
    opened: false,
    to: [buildRecipient('info@redlineautomall.ca')],
    tagIds: ['1']
  };
};

const contractToMail = (bill) => {
  const costSummaryParts = [
    bill.vehicleCost ? `Cost ${formatCurrencySummary(bill.vehicleCost)}` : null,
    bill.dealershipRepairCost ? `Repairs ${formatCurrencySummary(bill.dealershipRepairCost)}` : null,
    bill.totalVehicleCost ? `Total Cost ${formatCurrencySummary(bill.totalVehicleCost)}` : null,
    bill.grossProfit ? `Profit ${formatCurrencySummary(bill.grossProfit)}` : null
  ].filter(Boolean);
  const costContent = [
    `<p>Cost: ${bill.vehicleCost ? formatCurrencySummary(bill.vehicleCost) : 'N/A'}</p>`,
    bill.dealershipRepairCost ? `<p>Repairs: ${formatCurrencySummary(bill.dealershipRepairCost)}</p>` : '',
    bill.totalVehicleCost ? `<p>Total Cost: ${formatCurrencySummary(bill.totalVehicleCost)}</p>` : ''
  ].join('');

  return {
    category: 'contracts',
    content: `<p>${formatCurrencySummary(bill.sellingPrice)}</p><p>Vehicle: ${bill.vehicleYear || ''} ${bill.vehicleMake || ''} ${bill.vehicleModel || ''}</p>${costContent}`,
    subject: `${bill.vehicleYear || ''} ${bill.vehicleMake || ''} ${bill.vehicleModel || ''}`.trim() || 'Sales Contract',
    summary: `${formatCurrencySummary(bill.sellingPrice)}${costSummaryParts.length ? ` • ${costSummaryParts.join(' • ')}` : ''}${bill.stockNumber ? ` • Stock #${bill.stockNumber}` : ''}${bill.vinNumber ? ` • ${bill.vinNumber}` : ''}`,
    bill,
    date: moment(bill.createdAt).format('YYYY-MM-DD'),
    from: {
      name: `${bill.firstName || ''} ${bill.lastName || ''}`.trim() || 'Purchaser',
      email: `${bill.emailAddress || ''}`,
      avatar: null,
      avatarType: 'person',
      displayAvatar: 1
    },
    id: String(bill.id),
    opened: false,
    to: [buildRecipient('info@redlineautomall.ca')],
    tagIds: ['1']
  };
};

const rinToMail = (rinLog) => ({
  category: 'rin',
  content: `<p>${rinLog.vin}</p><p>Vehicle: ${rinLog.year || ''} ${rinLog.make || ''} ${rinLog.model || ''}</p>`,
  subject: `${rinLog.year || ''} ${rinLog.make || ''} ${rinLog.model || ''}`.trim() || 'RIN Record',
  summary: `${rinLog.status || 'Unknown'}${rinLog.vin ? ` • ${rinLog.vin}` : ''}${rinLog.soldToName ? ` • Sold To ${rinLog.soldToName}` : ''}`,
  rinLog,
  date: moment(rinLog.dateSold || rinLog.datePurchased || rinLog.createdAt).format('YYYY-MM-DD'),
  from: {
    name: `${rinLog.purchasedFromName || 'Source'}`,
    email: `${rinLog.vin}`,
    avatar: null,
    avatarType: 'vehicle',
    displayAvatar: 1
  },
  id: String(rinLog.id),
  opened: false,
  to: [buildRecipient('info@redlineautohaus.ca')],
  tagIds: ['1']
});

const initialState = {
  mails: {
    byId: {},
    allIds: []
  },
  inventoryList: [],
  tags: [],
  sidebarOpen: false,
  loading: true,
  singleLoading: false,
  rinLogLoading: false,
  rinLog: [],
  currentCategory: defaultCategory,
  listQueryByCategory: createDefaultQueryMap(),
  paginationByCategory: createDefaultPaginationMap(),
  dealershipMetrics: {
    unitsSold: 0,
    unitsWithCost: 0,
    unitsMissingCost: 0,
    totalSalesRevenue: 0,
    totalSalesVolume: 0,
    totalGrossProfit: 0,
    averageGrossProfit: 0,
    averageSalePrice: 0,
    averageDaysToSell: null,
    unitsWithTurnTime: 0,
    missingCostRate: 0
  },
  dealershipMetricsPeriod: 'current_month',
  dealershipMetricsLabel: 'Current Month',
  dealershipMetricsRangeStart: null,
  dealershipMetricsRangeEnd: null,
  dealershipMetricsCustomStartDate: null,
  dealershipMetricsCustomEndDate: null,
  dealershipMetricsComparison: {
    label: null,
    rangeStart: null,
    rangeEnd: null,
    summary: {
      unitsSold: 0,
      totalSalesRevenue: 0,
      totalGrossProfit: 0,
      averageDaysToSell: null
    },
    funnel: {
      financeLeads: 0,
      testDrives: 0,
      contractsWritten: 0,
      unitsSold: 0,
      soldFromCurrentPeriodContracts: 0,
      soldFromPriorPeriodContracts: 0,
      openContractsCreatedThisPeriod: 0,
      leadToDriveRate: null,
      driveToContractRate: null,
      contractToSaleRate: null,
      leadToContractRate: null,
      leadToSaleRate: null,
      currentPeriodContractCloseRate: null,
      carryoverCloseShare: null
    },
    exceptions: {
      summary: {
        soldUnitsMissingCost: 0,
        soldUnitsWithoutListingMatch: 0,
        soldUnitsMissingVin: 0,
        financeLeadsMissingContact: 0,
        testDrivesMissingContact: 0,
        leadsWithMissingContact: 0
      }
    }
  },
  dealershipMetricTrend: {
    bucketUnit: 'month',
    labels: [],
    unitsSold: [],
    totalSalesRevenue: [],
    totalGrossProfit: [],
    averageDaysToSell: []
  },
  dealershipFunnel: {
    financeLeads: 0,
    testDrives: 0,
    contractsWritten: 0,
    unitsSold: 0,
    soldFromCurrentPeriodContracts: 0,
    soldFromPriorPeriodContracts: 0,
    openContractsCreatedThisPeriod: 0,
    leadToDriveRate: null,
    driveToContractRate: null,
    contractToSaleRate: null,
    leadToContractRate: null,
    leadToSaleRate: null,
    currentPeriodContractCloseRate: null,
    carryoverCloseShare: null,
    strongestStep: null,
    weakestStep: null,
    trend: {
      bucketUnit: 'month',
      labels: [],
      financeLeads: [],
      testDrives: [],
      contractsWritten: [],
      unitsSold: [],
      leadToDriveRate: [],
      driveToContractRate: [],
      contractToSaleRate: [],
      leadToContractRate: [],
      leadToSaleRate: []
    }
  },
  dealershipMetricsSourceFilter: 'all',
  dealershipMetricAvailableSources: [],
  dealershipSourceAttribution: {
    summary: {
      financeLeadsAttributed: 0,
      testDrivesAttributed: 0,
      contractsWrittenAttributed: 0,
      soldUnitsAttributed: 0,
      unattributedFinanceLeads: 0,
      unattributedTestDrives: 0,
      unattributedContractsWritten: 0,
      unattributedSoldUnits: 0
    },
    topLeadSource: null,
    topSellingSource: null,
    bestClosingSource: null,
    fastestTurningSource: null,
    leaderboard: [],
    trend: {
      bucketUnit: 'month',
      labels: [],
      prospectSeries: [],
      soldSeries: []
    }
  },
  dealershipInventoryHealth: {
    activeInventoryCount: 0,
    staleInventoryCount: 0,
    averageDaysOnLot: null,
    oldestActiveUnit: null,
    agingBuckets: [],
    oldestActiveUnits: [],
    brandAgingLeaderboard: []
  },
  dealershipExceptions: {
    summary: {
      soldUnitsMissingCost: 0,
      soldUnitsWithoutListingMatch: 0,
      soldUnitsMissingVin: 0,
      activeInventoryMissingCost: 0,
      activeInventoryMissingCarfax: 0,
      activeInventoryMissingVin: 0,
      activeInventoryMissingMileage: 0,
      activeInventoryWithIssues: 0,
      financeLeadsMissingContact: 0,
      testDrivesMissingContact: 0,
      leadsWithMissingContact: 0
    },
    soldUnitGaps: [],
    inventoryGaps: [],
    leadGaps: []
  },
  dealershipInsights: {
    topSellingBrand: null,
    fastestTurningBrand: null,
    highestGrossingBrand: null,
    topSellingModel: null,
    topRevenueUnit: null,
    topGrossProfitUnit: null,
    brandLeaderboard: [],
    modelLeaderboard: []
  },
  dealershipMetricsLoading: false,
  dealershipMetricsError: null,
  error: null
};

const slice = createSlice({
  name: 'mail',
  initialState,
  reducers: {
    getTags(state, action) {
      const { tags } = action.payload;
      state.tags = tags;
    },
    setCurrentCategory(state, action) {
      state.currentCategory = action.payload || defaultCategory;
    },
    setListQuery(state, action) {
      const { category, query } = action.payload;
      if (!state.listQueryByCategory[category]) {
        state.listQueryByCategory[category] = { ...defaultQuery };
      }
      state.listQueryByCategory[category] = {
        ...state.listQueryByCategory[category],
        ...query
      };
    },
    getMails(state, action) {
      const { mails, category, pagination } = action.payload;

      state.mails.byId = {};
      state.mails.allIds = [];
      mails.forEach((mail) => {
        state.mails.byId[mail.id] = mail;
        state.mails.allIds.push(mail.id);
      });

      if (category && pagination) {
        state.paginationByCategory[category] = pagination;
      }

      state.loading = false;
      state.error = null;
    },
    getMail(state, action) {
      const { mail } = action.payload;

      state.mails.byId[mail.id] = mail;

      if (!state.mails.allIds.includes(mail.id)) {
        state.mails.allIds.push(mail.id);
      }

      state.singleLoading = false;
      state.error = null;
    },
    setMailsError(state, action) {
      state.error = action.payload || 'Unable to load records.';
      state.loading = false;
      state.singleLoading = false;
    },
    getRinLog(state, action) {
      const { rinLogs } = action.payload;
      state.rinLog = rinLogs;
      state.rinLogLoading = false;
    },
    setRinLogLoadingTrue(state) {
      state.rinLogLoading = true;
    },
    setSingleLoading(state, action) {
      state.singleLoading = Boolean(action.payload);
    },
    getInventory(state, action) {
      const { inventoryList } = action.payload;
      state.inventoryList = inventoryList;
    },
    setDealershipMetrics(state, action) {
      const payload = action.payload || {};
      const summary = payload.summary || payload;
      state.dealershipMetrics = {
        ...state.dealershipMetrics,
        ...summary
      };
      state.dealershipMetricsPeriod = payload.period || state.dealershipMetricsPeriod;
      state.dealershipMetricsLabel = payload.label || state.dealershipMetricsLabel;
      state.dealershipMetricsRangeStart = payload.rangeStart || state.dealershipMetricsRangeStart;
      state.dealershipMetricsRangeEnd = payload.rangeEnd || state.dealershipMetricsRangeEnd;
      if (payload.period === 'custom_range') {
        state.dealershipMetricsCustomStartDate = payload.rangeStart || state.dealershipMetricsCustomStartDate;
        state.dealershipMetricsCustomEndDate = payload.rangeEnd || state.dealershipMetricsCustomEndDate;
      }
      state.dealershipMetricsComparison = payload.comparison || state.dealershipMetricsComparison;
      state.dealershipMetricTrend = payload.trend || state.dealershipMetricTrend;
      state.dealershipFunnel = payload.funnel || state.dealershipFunnel;
      state.dealershipMetricsSourceFilter = payload.sourceFilter || 'all';
      state.dealershipMetricAvailableSources = payload.availableSources || [];
      state.dealershipSourceAttribution = payload.sourceAttribution || state.dealershipSourceAttribution;
      state.dealershipInventoryHealth = payload.inventoryHealth || state.dealershipInventoryHealth;
      state.dealershipExceptions = payload.exceptions || state.dealershipExceptions;
      state.dealershipInsights = payload.insights || state.dealershipInsights;
      state.dealershipMetricsLoading = false;
      state.dealershipMetricsError = null;
    },
    setDealershipMetricsLoading(state, action) {
      state.dealershipMetricsLoading = Boolean(action.payload);
      if (action.payload) {
        state.dealershipMetricsError = null;
      }
    },
    setDealershipMetricsPeriod(state, action) {
      state.dealershipMetricsPeriod = action.payload || 'current_month';
    },
    setDealershipMetricsError(state, action) {
      state.dealershipMetricsError = action.payload || 'Unable to load dealership statistics.';
      state.dealershipMetricsLoading = false;
    },
    setLoadingTrue(state) {
      state.loading = true;
      state.error = null;
    },
    openSidebar(state) {
      state.sidebarOpen = true;
    },
    closeSidebar(state) {
      state.sidebarOpen = false;
    }
  }
});

export const reducer = slice.reducer;

export const getTags = () => async (dispatch) => {
  const tags = [
    { id: 'inventory', type: 'category_tag', name: 'Inventory', newMails: 0 },
    { id: 'testdrives', type: 'category_tag', name: 'Test Drives', newMails: 0 },
    { id: 'financeleads', type: 'category_tag', name: 'Finance Leads', newMails: 0 },
    { id: 'contracts', type: 'category_tag', name: 'Sales Contracts', newMails: 0 },
    { id: 'statistics', type: 'category_tag', name: 'Statistics', newMails: 0 },
    { id: 'livechat', type: 'category_tag', name: 'Live Chat', newMails: 0 },
    { id: 'reports', type: 'category_tag', name: 'Tax Reports', newMails: 0 },
    { id: 'rin', type: 'category_tag', name: 'RIN Log', newMails: 0 }
  ];
  dispatch(slice.actions.getTags({ tags }));
};

export const setMailsQuery = (payload) => async (dispatch, getState) => {
  const category = payload?.categoryTag || getState().dealership.currentCategory || defaultCategory;
  const currentQuery = getState().dealership.listQueryByCategory[category] || defaultQuery;
  const nextQuery = {
    ...currentQuery,
    ...payload
  };

  delete nextQuery.categoryTag;

  if (
    payload?.search !== undefined ||
    payload?.pageSize !== undefined ||
    payload?.filter !== undefined ||
    payload?.source !== undefined ||
    payload?.brand !== undefined ||
    payload?.model !== undefined
  ) {
    nextQuery.page = payload?.page || 1;
  }

  dispatch(slice.actions.setListQuery({ category, query: nextQuery }));
  dispatch(getMails({ categoryTag: category }));
};

export const getMails = (params) => async (dispatch, getState) => {
  const category = params?.categoryTag || defaultCategory;
  const listQueryByCategory = getState().dealership.listQueryByCategory;
  const query = listQueryByCategory[category] || defaultQuery;
  const requestParams = {
    page: query.page,
    pageSize: query.pageSize,
    search: query.search || undefined,
    filter: query.filter && query.filter !== 'all' ? query.filter : undefined,
    source: query.source && query.source !== 'all' ? query.source : undefined,
    brand: query.brand || undefined,
    model: query.model || undefined
  };

  dispatch(slice.actions.setCurrentCategory(category));
  dispatch(slice.actions.setLoadingTrue());

  try {
    if (category === 'inventory') {
      const response = await axios.get(buildApiUrl('/dealership/inventory/get'), await getDealershipAuthConfig({
        params: requestParams
      }));

      const listings = Array.isArray(response?.data?.listings) ? response.data.listings : [];
      const mails = listings.map(inventoryToMail);
      const pagination = normalizePagination(response?.data?.pagination, query.page, query.pageSize, listings.length);

      dispatch(slice.actions.getMails({ mails, category, pagination }));
      return;
    }

    if (category === 'financeleads') {
      const response = await axios.get(buildApiUrl('/sales/finance-applications/get'), await getDealershipAuthConfig({
        params: requestParams
      }));

      const items = Array.isArray(response?.data)
        ? response.data
        : (Array.isArray(response?.data?.items) ? response.data.items : []);

      const mails = items.map(financeToMail);
      const pagination = normalizePagination(response?.data?.pagination, query.page, query.pageSize, items.length);
      dispatch(slice.actions.getMails({ mails, category, pagination }));
      return;
    }

    if (category === 'testdrives') {
      const response = await axios.get(buildApiUrl('/sales/test-drives/get'), await getDealershipAuthConfig({
        params: requestParams
      }));

      const items = Array.isArray(response?.data)
        ? response.data
        : (Array.isArray(response?.data?.items) ? response.data.items : []);

      const mails = items.map(testDriveToMail);
      const pagination = normalizePagination(response?.data?.pagination, query.page, query.pageSize, items.length);
      dispatch(slice.actions.getMails({ mails, category, pagination }));
      return;
    }

    if (category === 'contracts') {
      const response = await axios.get(buildApiUrl('/bill-of-sale/get'), await getDealershipAuthConfig({
        params: requestParams
      }));

      const items = Array.isArray(response?.data)
        ? response.data
        : (Array.isArray(response?.data?.items) ? response.data.items : []);

      const mails = items.map(contractToMail);
      const pagination = normalizePagination(response?.data?.pagination, query.page, query.pageSize, items.length);
      dispatch(slice.actions.getMails({ mails, category, pagination }));
      return;
    }

    if (category === 'rin') {
      const response = await axios.get(buildApiUrl('/rin-log/get'), await getDealershipAuthConfig({
        params: requestParams
      }));

      const items = Array.isArray(response?.data)
        ? response.data
        : (Array.isArray(response?.data?.items) ? response.data.items : []);

      const mails = items.map(rinToMail);
      const pagination = normalizePagination(response?.data?.pagination, query.page, query.pageSize, items.length);
      dispatch(slice.actions.getMails({ mails, category, pagination }));
      return;
    }

    if (category === 'statistics' || category === 'reports') {
      dispatch(slice.actions.getMails({
        mails: [],
        category,
        pagination: normalizePagination(null, 1, query.pageSize, 0)
      }));
      return;
    }

    dispatch(slice.actions.getMails({ mails: [], category, pagination: normalizePagination(null, 1, query.pageSize, 0) }));
  } catch (error) {
    dispatch(slice.actions.setMailsError(error?.message || 'Unable to load records.'));
  }
};

export const getMail = (mailboxCategory) => async (dispatch) => {
  const response = await axios.get('/api/mailbox/mail', {
    params: {
      mailboxCategory
    }
  });

  dispatch(slice.actions.getMail(response.data));
};

export const fetchDealershipRecord = createAsyncThunk(
  'dealership/fetchDealershipRecord',
  async function ({ category, id }, { dispatch, rejectWithValue }) {
    dispatch(slice.actions.setSingleLoading(true));

    try {
      if (category === 'inventory') {
        const response = await axios.get(buildApiUrl(`/dealership/inventory/${id}/get`), await getDealershipAuthConfig());
        const listing = response?.data?.listing || null;

        if (!listing) {
          throw new Error('Inventory record not found');
        }

        const mail = inventoryToMail(listing);
        dispatch(slice.actions.getMail({ mail }));
        return mail;
      }

      if (category === 'contracts') {
        const response = await axios.get(buildApiUrl(`/bill-of-sale/${id}/get`), await getDealershipAuthConfig());
        const bill = response?.data || null;

        if (!bill) {
          throw new Error('Sales contract not found');
        }

        const mail = contractToMail(bill);
        dispatch(slice.actions.getMail({ mail }));
        return mail;
      }

      if (category === 'financeleads') {
        const response = await axios.get(buildApiUrl(`/sales/finance-applications/${id}/get`), await getDealershipAuthConfig());
        const application = response?.data || null;

        if (!application) {
          throw new Error('Finance application not found');
        }

        const mail = financeToMail(application);
        dispatch(slice.actions.getMail({ mail }));
        return mail;
      }

      if (category === 'testdrives') {
        const response = await axios.get(buildApiUrl(`/sales/test-drives/${id}/get`), await getDealershipAuthConfig());
        const testDrive = response?.data || null;

        if (!testDrive) {
          throw new Error('Test drive request not found');
        }

        const mail = testDriveToMail(testDrive);
        dispatch(slice.actions.getMail({ mail }));
        return mail;
      }

      throw new Error('Unsupported dealership record category');
    } catch (error) {
      dispatch(slice.actions.setSingleLoading(false));
      return rejectWithValue(error?.message || 'Unable to load dealership record');
    }
  }
);

export const openSidebar = () => async (dispatch) => {
  dispatch(slice.actions.openSidebar());
};

export const closeSidebar = () => async (dispatch) => {
  dispatch(slice.actions.closeSidebar());
};

export const getRinLog = () => async (dispatch) => {
  dispatch(slice.actions.setRinLogLoadingTrue());
  const rinResponse = await axios.get(buildApiUrl('/rin-log/get'), await getDealershipAuthConfig());
  const rinLogs = Array.isArray(rinResponse.data) ? rinResponse.data : rinResponse.data.items;
  dispatch(slice.actions.getRinLog({ rinLogs: rinLogs || [] }));
};

export const getDealershipMetrics = (requestedFilters) => async (dispatch, getState) => {
  const filters = typeof requestedFilters === 'string'
    ? { period: requestedFilters }
    : (requestedFilters || {});
  const dealershipState = getState().dealership;
  let period = filters.period || dealershipState.dealershipMetricsPeriod || 'current_month';
  const startDate = filters.startDate || dealershipState.dealershipMetricsCustomStartDate;
  const endDate = filters.endDate || dealershipState.dealershipMetricsCustomEndDate;
  const source = String(filters.source || 'all').trim() || 'all';

  if (period === 'custom_range' && (!startDate || !endDate)) {
    period = 'current_month';
  }

  dispatch(slice.actions.setDealershipMetricsPeriod(period));
  dispatch(slice.actions.setDealershipMetricsLoading(true));

  try {
    const response = await axios.get(buildApiUrl('/dealership/metrics/get'), await getDealershipAuthConfig({
      params: {
        period,
        source,
        ...(period === 'custom_range'
          ? {
            startDate,
            endDate
          }
          : {})
      }
    }));
    dispatch(slice.actions.setDealershipMetrics(response.data || {}));
  } catch (error) {
    dispatch(slice.actions.setDealershipMetricsError(
      error?.response?.data?.message ||
      error?.message ||
      'Unable to load dealership statistics.'
    ));
  }
};

export const addInventory = (payload) => async (dispatch) => {
  await axios.post(buildApiUrl('/sales/vehicle/new'), payload, await getDealershipAuthConfig());
  dispatch(getMails({ categoryTag: 'inventory' }));
  dispatch(getInventoryList());
  dispatch(getDealershipMetrics());
};

export const updateInventory = (payload) => async (dispatch) => {
  await axios.post(buildApiUrl('/sales/vehicle/new/edit'), payload, await getDealershipAuthConfig());
  dispatch(getMails({ categoryTag: 'inventory' }));
  dispatch(getInventoryList());
  dispatch(getDealershipMetrics());
  return 'success';
};

export const createInventoryViewingReservation = createAsyncThunk(
  'dealership/createInventoryViewingReservation',
  async function (payload, { dispatch, rejectWithValue }) {
    try {
      const idToken = await getOptionalIdToken();
      const response = await axios.post(buildApiUrl('/dealership/inventory/reservations/create'), {
        ...payload,
        ...(idToken ? { idToken } : {})
      });

      dispatch(getMails({ categoryTag: 'inventory' }));
      return response.data || {};
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        'Unable to save viewing reservation'
      );
    }
  }
);

export const cancelInventoryViewingReservation = createAsyncThunk(
  'dealership/cancelInventoryViewingReservation',
  async function (payload, { dispatch, rejectWithValue }) {
    try {
      const idToken = await getOptionalIdToken();
      const response = await axios.post(
        buildApiUrl(`/dealership/inventory/reservations/${payload.reservationId}/cancel`),
        {
          ...(idToken ? { idToken } : {})
        }
      );

      dispatch(getMails({ categoryTag: 'inventory' }));
      return response.data || {};
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message ||
        error?.message ||
        'Unable to release viewing reservation'
      );
    }
  }
);

export const createSalesContract = (payload) => async (dispatch) => {
  await axios.post(buildApiUrl('/bill-of-sale/new'), payload, await getDealershipAuthConfig());
  dispatch(getMails({ categoryTag: 'contracts' }));
  dispatch(getDealershipMetrics());
  return 'success';
};

export const updateSalesContract = (payload) => async (dispatch) => {
  await axios.post(buildApiUrl('/bill-of-sale/edit'), payload, await getDealershipAuthConfig());
  dispatch(getMails({ categoryTag: 'contracts' }));
  dispatch(getDealershipMetrics());
  return 'success';
};

export const deleteSalesContract = (id) => async (dispatch) => {
  await axios.get(buildApiUrl(`/bill-of-sale/${id}/delete`), await getDealershipAuthConfig());
  dispatch(getMails({ categoryTag: 'contracts' }));
  dispatch(getDealershipMetrics());
  return 'success';
};

export const setListingStatus = createAsyncThunk(
  'dealership/setListingStatus',
  async function (input, { dispatch, rejectWithValue, requestId, signal }) {
    const scope = currentListingStatusScope();
    try {
      if (!scope) throw new Error('Sign in to your shop before changing listing status.');
      const idToken = await firebase.auth().currentUser.getIdToken();
      const connection = socketClient.socket;
      const isCurrent = () => !signal.aborted && currentListingStatusScope() === scope
        && socketClient.socket === connection && connection?.connected
        && socketClient.authToken === idToken && Number(socketClient.shopId) === Number(getActiveShopId());
      if (!isCurrent()) throw new Error('Reconnect to your shop before changing listing status.');
      const saved = prepareListingStatusIntent(scope, input);
      const response = await emitListingStatusRequest({
        connection, signal, isCurrent,
        payload: { ...saved.payload, idToken, requestId }
      });
      if (!isCurrent()) throw new Error('Account changed. Reopen this listing.');
      const operation = recordListingStatusResult(scope, saved.payload, response?.operation);
      dispatch(getMails({ categoryTag: 'inventory' }));
      dispatch(getInventoryList());
      dispatch(getDealershipMetrics());
      return { operation };
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to confirm listing status. Check the saved request.');
    }
  }
);

export const uploadPhoto = createAsyncThunk(
  'dealership/uploadPhoto',
  async function (payload, { dispatch, rejectWithValue }) {
    try {
      const file = payload.file;
      const currentListingId = payload.currentListingId;
      const storageRef = firebase.storage().ref();
      const fileRef = storageRef.child(`${file.name} ${moment().format('YYYYMMDDHHmmss')}`);
      await fileRef.put(file);
      const currentUrl = await fileRef.getDownloadURL();
      const requestId = buildRequestId(`listing-photo:upload:${currentListingId}`);
      const idToken = await getOptionalIdToken();

      const response = await emitScopedDealershipRequest({
        emitEvent: 'uploadListingPhoto',
        successEvent: 'uploadListingPhotoSuccess',
        errorEvent: 'uploadListingPhotoError',
        payload: {
          url: currentUrl,
          vehicle_id: currentListingId,
          ...(idToken ? { idToken } : {}),
          requestId
        },
        timeoutMessage: 'Timed out while uploading listing photo'
      });

      dispatch(getMails({ categoryTag: 'inventory' }));
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to upload listing photo');
    }
  }
);

export const getInventoryList = createAsyncThunk(
  'dealership/getInventoryList',
  async function (_, { dispatch, rejectWithValue }) {
    try {
      const requestId = buildRequestId('inventory:list');
      const idToken = await getOptionalIdToken();

      const inventoryList = await emitScopedDealershipRequest({
        emitEvent: 'getInventory',
        successEvent: 'getInventorySuccess',
        errorEvent: 'getInventoryError',
        payload: {
          ...(idToken ? { idToken } : {}),
          requestId
        },
        timeoutMessage: 'Timed out while loading inventory list',
        mapSuccess: normalizeInventoryListPayload
      });

      dispatch(slice.actions.getInventory({ inventoryList }));
      return inventoryList;
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to load inventory list');
    }
  }
);

export const deleteListingPhoto = createAsyncThunk(
  'dealership/deleteListingPhoto',
  async function (payload, { dispatch, rejectWithValue }) {
    try {
      const requestId = buildRequestId(`listing-photo:delete:${payload.photo_id}`);
      const idToken = await getOptionalIdToken();

      const response = await emitScopedDealershipRequest({
        emitEvent: 'deleteListingPhoto',
        successEvent: 'deleteListingPhotoSuccess',
        errorEvent: 'deleteListingPhotoError',
        payload: {
          photo_id: payload.photo_id,
          vehicle_id: payload.vehicle_id,
          ...(idToken ? { idToken } : {}),
          requestId
        },
        timeoutMessage: 'Timed out while deleting listing photo'
      });

      dispatch(getMails({ categoryTag: 'inventory' }));
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to delete listing photo');
    }
  }
);

export const reorderListingPhotos = createAsyncThunk(
  'dealership/reorderListingPhotos',
  async function (payload, { dispatch, rejectWithValue }) {
    try {
      const requestId = buildRequestId(`listing-photo:reorder:${payload.vehicle_id}`);
      const idToken = await getOptionalIdToken();

      const response = await emitScopedDealershipRequest({
        emitEvent: 'reorderListingPhotos',
        successEvent: 'reorderListingPhotosSuccess',
        errorEvent: 'reorderListingPhotosError',
        payload: {
          vehicle_id: payload.vehicle_id,
          photoIds: payload.photoIds,
          ...(idToken ? { idToken } : {}),
          requestId
        },
        timeoutMessage: 'Timed out while reordering listing photos'
      });

      dispatch(getMails({ categoryTag: 'inventory' }));
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to reorder listing photos');
    }
  }
);

export const deleteListing = createAsyncThunk(
  'dealership/deleteListing',
  async function (payload, { dispatch, rejectWithValue }) {
    try {
      const requestId = buildRequestId(`listing:delete:${payload.listing_id}`);
      const idToken = await getOptionalIdToken();

      const response = await emitScopedDealershipRequest({
        emitEvent: 'deleteListing',
        successEvent: 'deleteListingSuccess',
        errorEvent: 'deleteListingError',
        payload: {
          ...payload,
          ...(idToken ? { idToken } : {}),
          requestId
        },
        timeoutMessage: 'Timed out while deleting listing'
      });

      dispatch(getMails({ categoryTag: 'inventory' }));
      dispatch(getInventoryList());
      dispatch(getDealershipMetrics());
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Unable to delete listing');
    }
  }
);

export default slice;
